/**
 * React Query Hooks for Workflow Data
 *
 * Provides data fetching hooks for correspondence workflows
 * Uses React Query for caching, background updates, and optimistic updates
 * Follows existing patterns from use-query.ts
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { gatedAction } from '@/lib/api/auth-gate';
import { WorkflowDataService } from '@/lib/services/correspondences/workflows/workflow-data.service';
import { collectSendFailureReasons } from '@/lib/services/correspondences/workflows/services/correspondence.service';
import { dispatchCorrespondenceWorkflowCompleted } from '@/lib/events/correspondence-events';
import {
  correspondenceKeys,
  invalidateAfterCorrespondenceSent
} from '@/lib/query-keys';
import type {
  WorkflowData,
  CorrespondenceDraft,
  BulkSendResults,
  WorkflowType
} from '@/lib/services/correspondences/workflows/workflow-types';

// ============================================================================
// QUERY KEYS (using centralized factory)
// ============================================================================

/**
 * @deprecated Use correspondenceKeys from @/lib/query-keys instead
 * Kept for backward compatibility during migration
 */
export const workflowQueryKeys = {
  all: ['workflow'] as const,

  // Workflow data - maps to correspondenceKeys.workflowData
  workflowData: (matterId: number, type: WorkflowType) =>
    correspondenceKeys.workflowData(matterId, type),

  // Drafts - maps to correspondenceKeys.drafts
  drafts: (matterId: number) => correspondenceKeys.drafts(matterId),

  // Correspondence - maps to correspondenceKeys.workflow
  correspondences: (matterId: number) => correspondenceKeys.workflow(matterId)
};

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Fetch workflow data based on type
 * Includes institutions, documents, signatories, and law firm data
 */
export function useWorkflowData(
  matterId: number,
  type: WorkflowType,
  options?: UseQueryOptions<WorkflowData>
) {
  return useQuery({
    queryKey: workflowQueryKeys.workflowData(matterId, type),
    queryFn: () => {
      // Use server actions instead of client-side API calls
      if (type === 'notification') {
        return gatedAction(() =>
          WorkflowDataService.getNotificationWorkflowDataAction(matterId)
        );
      } else {
        return gatedAction(() =>
          WorkflowDataService.getCollectionWorkflowDataAction(matterId)
        );
      }
    },
    enabled: !!matterId && !!type,
    staleTime: 0, // Always refetch - workflow data must be fresh (correspondence letters regenerated by backend)
    retry: (failureCount, error: unknown) => {
      // Don't retry on 4xx errors
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? (error as { status?: number }).status
          : undefined;
      if (status !== undefined && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    ...options
  });
}

/**
 * Create bulk draft correspondences
 */
export function useCreateBulkDrafts(
  options?: UseMutationOptions<
    CorrespondenceDraft[],
    Error,
    {
      matterId: number;
      type: WorkflowType;
      institutionIds: number[];
    }
  >
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matterId, type, institutionIds }) => {
      // Use server action instead of client-side API call
      const result = await WorkflowDataService.createBulkDraftsAction(
        matterId,
        type,
        institutionIds
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to create drafts');
      }
      return result.data!;
    },
    onSuccess: (_data, variables) => {
      // Invalidate drafts query
      queryClient.invalidateQueries({
        queryKey: workflowQueryKeys.drafts(variables.matterId)
      });
    },
    onError: (error) => {
      console.error('Failed to create drafts:', error);
      toast.error('Failed to create draft correspondence. Please try again.');
    },
    ...options
  });
}

/**
 * Update a correspondence draft
 * Uses optimistic updates for better UX
 */
export function useUpdateDraft(matterId: number) {
  const queryClient = useQueryClient();

  return useMutation<
    CorrespondenceDraft,
    Error,
    {
      correspondenceId: number;
      updates: Partial<CorrespondenceDraft>;
    },
    { previousDrafts: CorrespondenceDraft[] | undefined }
  >({
    mutationFn: async ({ correspondenceId, updates }) => {
      // Use server action instead of client-side API call
      const result = await WorkflowDataService.updateDraftAction(
        matterId,
        correspondenceId,
        updates
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to update draft');
      }
      return result.data!;
    },
    // Optimistic update
    onMutate: async ({ correspondenceId, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: workflowQueryKeys.drafts(matterId)
      });

      // Snapshot previous value
      const previousDrafts = queryClient.getQueryData<CorrespondenceDraft[]>(
        workflowQueryKeys.drafts(matterId)
      );

      // Optimistically update
      if (previousDrafts) {
        queryClient.setQueryData<CorrespondenceDraft[]>(
          workflowQueryKeys.drafts(matterId),
          previousDrafts.map((draft) =>
            draft.id === correspondenceId
              ? { ...draft, ...updates, lastModified: new Date().toISOString() }
              : draft
          )
        );
      }

      return { previousDrafts };
    },
    // Rollback on error
    onError: (err, variables, context) => {
      if (context?.previousDrafts) {
        queryClient.setQueryData(
          workflowQueryKeys.drafts(matterId),
          context.previousDrafts
        );
      }
      toast.error('Failed to save changes. Please try again.');
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: workflowQueryKeys.drafts(matterId)
      });
    }
  });
}

interface SendCorrespondencesOptions extends UseMutationOptions<
  BulkSendResults,
  Error,
  { correspondenceIds: number[]; sendSenderCopy?: boolean }
> {
  /**
   * Label for the single toast shown while the send is in flight.
   * Defaults to a generic "Sending N correspondence(s)…".
   *
   * Ignored when `silentToast` is true.
   */
  pendingToastLabel?: (count: number) => string;
  /**
   * Suppress every toast managed by this hook (loading, success, warning).
   * Errors are still re-thrown so callers can handle them.
   *
   * Use this when the caller wants to drive the toast lifecycle from the
   * BFF data (so the toast tracks real row state, not a fixed timer).
   */
  silentToast?: boolean;
}

/**
 * Send correspondences.
 *
 * Shows a single transient toast while the request is in flight (no fake
 * "Successfully sent" message — the workflow runs asynchronously after this
 * call returns, so claiming success here would be a lie).
 */
export function useSendCorrespondences(
  matterId: number,
  options?: SendCorrespondencesOptions
) {
  const queryClient = useQueryClient();
  const { pendingToastLabel, silentToast, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async ({ correspondenceIds, sendSenderCopy }) => {
      const count = correspondenceIds.length;
      const label = pendingToastLabel
        ? pendingToastLabel(count)
        : `Sending ${count} correspondence${count !== 1 ? 's' : ''}…`;
      const toastId = silentToast ? undefined : toast.loading(label);

      try {
        const result = await WorkflowDataService.sendCorrespondencesAction(
          matterId,
          correspondenceIds,
          sendSenderCopy
        );
        if (!result.success) {
          throw new Error(result.error || 'Failed to send correspondences');
        }
        const results = result.data!;

        if (silentToast) return results;

        if (results && results.failed > 0 && results.sent > 0) {
          toast.warning(
            `${results.sent} of ${results.total} started. ${results.failed} failed. Check the rows that didn't move to In Progress.`,
            { id: toastId, duration: 10000 }
          );
        } else if (results && results.failed > 0) {
          // Surface the real backend reason (e.g. missing letterhead /
          // signatory signature) instead of a generic string that leaves
          // the user with no actionable next step (LF-4629).
          const reasons = collectSendFailureReasons(results.results);
          toast.error(
            reasons.length > 0
              ? `Could not start sending: ${reasons.join('; ')}`
              : 'Could not start sending. Please check the errors and try again.',
            { id: toastId, duration: 10000 }
          );
        } else {
          // BE returns within ~300ms (it just enqueues the Temporal workflow).
          // Convert to a finished-state toast that auto-dismisses after a
          // comfortable read window — the row "Sending…" pill carries the
          // rest of the in-flight signal.
          toast.success(label.replace(/…$/, ''), {
            id: toastId,
            duration: 4000
          });
        }

        return results;
      } catch (error) {
        if (!silentToast) {
          toast.error('Could not start sending. Please try again.', {
            id: toastId,
            duration: 8000
          });
        }
        throw error;
      }
    },
    onSuccess: () => {
      // Fire-and-forget: don't await so mutateAsync resolves immediately,
      // allowing callers to set optimistic overlay before stale refetch arrives
      invalidateAfterCorrespondenceSent(queryClient, matterId);

      // Signal UI (e.g., NextStepsCard) to refresh immediately
      dispatchCorrespondenceWorkflowCompleted({ matterId });
    },
    ...mutationOptions
  });
}
